import { cycleInfo, cyclePhase, cycleRegularity, hormonal, type PhaseId } from "./cycle";
import { cycleGate } from "./cycleCare";
import { patternsFor } from "./cyclePatterns";
import {
  IRON_ROW,
  NO_PHASES,
  PANEL_WORDS,
  PHASE_NOTE,
  PHASE_ORDER,
  PHASE_WORDS,
  centreDay,
  centreNext,
  daysWord,
  phaseEasy,
  phaseGist,
  phaseLine,
  ringLabel,
  whenWord,
  type NoPhasesWhy,
} from "./cycleWords";
import { addDays, dayLabel, parseDay } from "./date";
import { hasCondition } from "./eating";
import type { KayaState } from "./types";

/**
 * Progress › Cycle's ring and its phase panel as figures and words (ADR-059). The component draws
 * what this says and does no arithmetic of its own; the phases come from `cycle.ts::cyclePhase`'s
 * ranges and the gate from `cycleCare.ts::cycleGate`, so the ring can never disagree with the
 * session rules about whether a phase may be named.
 */

/** "rest" is the one plain arc after the bleed days when there are no phases to show. */
export type RingArcId = PhaseId | "rest";

export interface RingArc {
  id: RingArcId;
  /** cycle days, inclusive; the arcs tile 1..len in order with no overlap */
  from: number;
  to: number;
}

export interface RingNext {
  id: PhaseId;
  startsDay: number;
  /** YYYY-MM-DD, an estimate */
  date: string;
  inDays: number;
}

export interface CycleRing {
  len: number;
  day: number;
  arcs: RingArc[];
  ovulationDay: number | null;
  /** where today's marker sits, 0..1 clockwise from day 1 at the top: the middle of today's day */
  todayFrac: number;
  current: PhaseId | null;
  next: RingNext | null;
  phased: boolean;
  why: NoPhasesWhy | null;
  whyNoPhases: string | null;
  ariaLabel: string;
  centre: { title: string; lines: string[] };
}

/** Why the gate says "symptoms", in the gate's own order. */
function noPhasesWhy(S: KayaState, today: string): NoPhasesWhy {
  if (hormonal(S)) return "hormonal";
  if (hasCondition(S, "pcos")) return "pcos";
  const reg = cycleRegularity(S, parseDay(today).getTime());
  return reg.irregular ? "irregular" : "late";
}

/** Clip `cyclePhase`'s ranges so they tile 1..len: a long period setting can overrun a short cycle. */
function tile(ranges: [PhaseId, number, number][], len: number): RingArc[] {
  const out: RingArc[] = [];
  let next = 1;
  for (const [id, from, to] of ranges) {
    const f = Math.max(from, next);
    const t = Math.min(to, len);
    if (t < f) continue;
    out.push({ id, from: f, to: t });
    next = t + 1;
  }
  if (out.length && out[out.length - 1].to < len) out[out.length - 1].to = len;
  return out;
}

/**
 * The ring for `today`, or null when there is nothing to draw: tracking off, periods stopped, a
 * man's profile, a pregnancy (the page says what it says today), or no period logged yet.
 */
export function cycleRing(S: KayaState, today: string): CycleRing | null {
  const gate = cycleGate(S, today);
  if (gate === "off" || gate === "pregnant") return null;
  const ci = cycleInfo(S, today);
  const cp = cyclePhase(S, today);
  if (!ci || ci.day === null || !cp) return null;

  const len = cp.len;
  const day = Math.min(cp.day, len);
  // a period logged as running past the set length is still a period day
  const bleedTo = Math.max(cp.ranges.menstrual[1], ci.inPeriod ? day : 0);
  const todayFrac = (day - 0.5) / len;
  const phased = gate === "phases";

  if (!phased) {
    const arcs = tile(
      [
        ["menstrual", 1, bleedTo],
        ["follicular", bleedTo + 1, len],
      ],
      len
    ).map((a): RingArc => (a.id === "menstrual" ? a : { ...a, id: "rest" }));
    const bleed = arcs[0];
    const why = noPhasesWhy(S, today);
    return {
      len,
      day,
      arcs,
      ovulationDay: null,
      todayFrac,
      current: day <= bleed.to ? "menstrual" : null,
      next: null,
      phased: false,
      why,
      whyNoPhases: NO_PHASES[why],
      ariaLabel: ringLabel({ day, len, period: [bleed.from, bleed.to], ovulationDay: null, luteal: null, current: null }),
      centre: { title: centreDay(day, len), lines: [] },
    };
  }

  const r = cp.ranges;
  const arcs = tile(
    [
      ["menstrual", r.menstrual[0], bleedTo],
      ["follicular", r.follicular[0], r.follicular[1]],
      ["ovulation", r.ovulation[0], r.ovulation[1]],
      ["luteal", r.luteal[0], r.luteal[1]],
    ],
    len
  );
  // The phase is the arc today sits in, so the centre and the marker never disagree — the one
  // place this differs from `cyclePhase` is a predicted, not yet logged, period day.
  const idx = Math.max(0, arcs.findIndex((a) => day >= a.from && day <= a.to));
  const here = arcs[idx];
  const current = here.id as PhaseId;
  const wraps = idx === arcs.length - 1;
  const after = arcs[(idx + 1) % arcs.length];
  const inDays = wraps ? (ci.daysToNext ?? len - day + 1) : after.from - day;
  const next: RingNext = {
    id: after.id as PhaseId,
    startsDay: after.from,
    date: wraps && ci.next ? ci.next : addDays(today, inDays),
    inDays,
  };
  const ovulationDay = r.ovulation[0] + 1;
  const luteal = arcs.find((a) => a.id === "luteal");
  const bleed = arcs[0];

  return {
    len,
    day,
    arcs,
    ovulationDay: ovulationDay <= len ? ovulationDay : null,
    todayFrac,
    current,
    next,
    phased: true,
    why: null,
    whyNoPhases: null,
    ariaLabel: ringLabel({
      day,
      len,
      period: [bleed.from, bleed.to],
      ovulationDay,
      luteal: luteal ? [luteal.from, luteal.to] : null,
      current,
    }),
    centre: { title: PHASE_WORDS[current].title, lines: centreNext(next.id, dayLabel(next.date)) },
  };
}

export interface PanelNext {
  id: PhaseId;
  /** "Next · Period · ~Sun 11 Oct, in 6 days est." — the date only for the real next phase */
  heading: string;
  line: string;
  /** the first sentence of the phase's `what` */
  gist: string;
}

export interface PhasePanel {
  id: PhaseId;
  /** the phase shown is today's — false once another is tapped, which offers Back to today */
  isToday: boolean;
  heading: string;
  /** with no phases: why, first */
  why: string | null;
  what: string;
  good: string[];
  easy: string[];
  /** her own patterns that begin in the shown phase's days, "Breast pain · days 22–25" — her
   *  data beside the phase's general words, as the water swing is; null when there are none
   *  (ADR-060) */
  forYou: string[] | null;
  /** the Iron row to Progress › Food, on the period panel */
  iron: { title: string; line: string } | null;
  next: PanelNext | null;
  /** the chapter's rule, under the group; none when there are no phases to set it against */
  note: string | null;
}

/**
 * The words under the ring for the phase `selected` (today's phase when omitted or not on the
 * ring). Every string is `cycleWords.ts`'s; `waterKg` is her own pre-period swing on the scale
 * (`cycleView.ts::waterSwing`), named in the week before's words when she has one.
 */
export function phasePanel(
  S: KayaState,
  today: string,
  selected?: PhaseId | null,
  waterKg?: number | null
): PhasePanel | null {
  const ring = cycleRing(S, today);
  if (!ring) return null;
  const arcOf = (id: PhaseId) => ring.arcs.find((a) => a.id === id);
  const { patterns } = patternsFor(S, today);
  const forYou = (from: number, to: number): string[] | null => {
    const lines = patterns
      .filter((p) => p.from >= from && p.from <= to)
      .map((p) => `${p.label} · ${daysWord(p.from, p.to)}`);
    return lines.length ? lines : null;
  };

  if (!ring.phased) {
    const bleed = ring.arcs[0];
    const w = PHASE_WORDS.menstrual;
    return {
      id: "menstrual",
      isToday: ring.current === "menstrual",
      heading: `${w.title} · ${daysWord(bleed.from, bleed.to)}`,
      why: ring.whyNoPhases,
      what: w.what,
      good: [...w.good],
      easy: phaseEasy("menstrual", waterKg),
      forYou: forYou(bleed.from, bleed.to),
      iron: null,
      next: null,
      note: null,
    };
  }

  const current = ring.current ?? "menstrual";
  const id = selected && arcOf(selected) ? selected : current;
  const isToday = id === current;
  const arc = arcOf(id)!;
  const w = PHASE_WORDS[id];
  const days = daysWord(arc.from, arc.to);

  const order = ring.arcs.map((a) => a.id as PhaseId);
  const nid = order[(order.indexOf(id) + 1) % order.length] ?? PHASE_ORDER[0];
  const when = isToday && ring.next ? whenWord(dayLabel(ring.next.date), ring.next.inDays) : null;

  return {
    id,
    isToday,
    heading: isToday ? `${PANEL_WORDS.thisPhase} · ${w.title} · ${days}` : `${w.title} · ${days}`,
    why: null,
    what: w.what,
    good: [...w.good],
    easy: phaseEasy(id, waterKg),
    forYou: forYou(arc.from, arc.to),
    iron: id === "menstrual" ? { ...IRON_ROW } : null,
    next: {
      id: nid,
      heading: `${PANEL_WORDS.next} · ${PHASE_WORDS[nid].title}${when ? ` · ${when}` : ""}`,
      line: phaseLine(nid, waterKg),
      gist: phaseGist(nid),
    },
    note: PHASE_NOTE,
  };
}
